import React, { useState } from 'react';
import { Check, X } from 'lucide-react';
import ActionLinks from '../components/ActionLinks';
import Pagination from '../components/Pagination';
import TableLayout from '../components/TableLayout';

// 测评批次管理页面
// 页面结构：结果卡片（操作按钮 + 批次列表 + 分页）
export default function BatchManagementPage() {
  // ================= 页面状态 =================
  const [drawerMode, setDrawerMode] = useState('');

  // ================= 表格配置 =================
  const tableHeaders = [
    '测评学年',
    '测评学期',
    '批次名称',
    '测评开始时间',
    '测评结束时间',
    '状态',
    '操作',
  ];

  // 表格演示数据：后续接接口时，把这里替换成接口返回数据即可。
  const tableData = [
    [
      '2025-2026',
      '第一学期',
      '2025秋季综合测评批次',
      '2025-09-01 00:00',
      '2025-10-15 23:59',
      <span className="text-green-600 border border-green-200 bg-green-50 px-2 py-0.5 rounded text-xs">
        启用
      </span>,
      <ActionLinks
        links={[
          { text: '修改', onClick: () => setDrawerMode('edit') },
        ]}
      />,
    ],
    [
      '2024-2025',
      '第二学期',
      '2024春季综合测评批次',
      '2025-03-01 00:00',
      '2025-04-15 23:59',
      <span className="text-gray-500 border border-gray-200 bg-gray-50 px-2 py-0.5 rounded text-xs">
        禁用
      </span>,
      <ActionLinks
        links={[
          { text: '修改', onClick: () => setDrawerMode('edit') },
        ]}
      />,
    ],
  ];

  return (
    <div className="animate-fade-in flex flex-col h-full">
      {/* 结果卡片：承载页面主要列表和操作按钮 */}
      <div className="bg-white rounded shadow-sm border border-gray-100 p-4 flex-1">
        {/* 表格上方操作按钮区 */}
        <div className="flex space-x-3 mb-4">
          <button
            onClick={() => setDrawerMode('add')}
            className="px-4 py-2 bg-[#1890ff] text-white rounded text-sm transition-colors"
          >
            新增
          </button>
          <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50 transition-colors">
            删除
          </button>
        </div>

        {/* 批次列表表格 */}
        <TableLayout
          selectable
          headers={tableHeaders}
          data={tableData}
        />

        {/* 分页区 */}
        <Pagination count={tableData.length} />
      </div>

      {/* 右侧抽屉：新增/修改测评批次 */}
      {drawerMode && (
        <BatchFormDrawer
          mode={drawerMode}
          onClose={() => setDrawerMode('')}
        />
      )}
    </div>
  );
}

// 新增/修改测评批次抽屉
function BatchFormDrawer({ mode, onClose }) {
  // ================= 抽屉状态 =================
  const [activeStep, setActiveStep] = useState(1);
  const [noticeEnabled, setNoticeEnabled] = useState('否');

  const studentScopeList = [
    {
      id: 1,
      grade: '2024',
      studentType: '专科生',
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30">
      <div
        className="h-full bg-white shadow-[-8px_0_24px_rgba(0,0,0,0.16)] flex flex-col"
        style={{ width: 'min(1120px, calc(100vw - 280px))' }}
      >
        {/* 抽屉标题栏 */}
        <div className="h-[76px] px-8 border-b border-gray-200 flex items-center gap-5">
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
          <h2 className="text-[20px] font-bold text-gray-900">
            {mode === 'add' ? '新增批次' : '修改批次'}
          </h2>
        </div>

        {/* 步骤条 */}
        <div className="px-10 pt-8">
          <div className="flex items-center gap-5">
            <StepNode
              number={1}
              title="基本信息"
              active={activeStep === 1}
              completed={activeStep > 1}
            />
            <div className={`h-px flex-1 ${activeStep > 1 ? 'bg-[#1890ff]' : 'bg-gray-200'}`} />
            <StepNode
              number={2}
              title="参评学生设置"
              active={activeStep === 2}
              completed={false}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-10 py-8">
          {activeStep === 1 && (
            <BatchBaseInfoStep
              mode={mode}
              noticeEnabled={noticeEnabled}
              setNoticeEnabled={setNoticeEnabled}
            />
          )}

          {activeStep === 2 && (
            <BatchStudentScopeStep studentScopeList={studentScopeList} />
          )}
        </div>

        {/* 抽屉底部按钮 */}
        <div className="h-[72px] px-10 border-t border-gray-100 flex items-center gap-3">
          {activeStep === 1 ? (
            <button
              onClick={() => setActiveStep(2)}
              className="px-6 py-2.5 bg-[#1890ff] text-white rounded text-sm hover:bg-[#096dd9]"
            >
              保存并编辑下一步
            </button>
          ) : (
            <>
              <button
                onClick={() => setActiveStep(1)}
                className="px-6 py-2.5 bg-[#1890ff] text-white rounded text-sm hover:bg-[#096dd9]"
              >
                上一步
              </button>
              <button
                onClick={onClose}
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50"
              >
                关闭
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// 步骤节点
function StepNode({ number, title, active, completed }) {
  return (
    <div className="flex items-center gap-3 shrink-0">
      <span className={`w-10 h-10 rounded-full flex items-center justify-center text-[16px] ${active ? 'bg-[#1890ff] text-white' : completed ? 'bg-[#eaf4ff] text-[#1890ff]' : 'bg-gray-100 text-gray-400'}`}>
        {completed ? <Check size={18} /> : number}
      </span>
      <span className={`text-[16px] ${active || completed ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
        {title}
      </span>
    </div>
  );
}

// 第一步：基本信息
function BatchBaseInfoStep({ mode, noticeEnabled, setNoticeEnabled }) {
  return (
    <div className="max-w-[1000px]">
      <h3 className="text-[16px] font-bold text-gray-900 mb-7">
        基本信息
      </h3>

      <div className="space-y-5">
        <RequiredField label="批次名称">
          <input
            defaultValue={mode === 'edit' ? '2025秋季综合测评批次' : ''}
            placeholder="请输入"
            className="w-full border border-gray-300 rounded px-3 py-2.5 text-sm outline-none focus:border-[#1890ff]"
          />
        </RequiredField>

        <RequiredField label="是否启用">
          <RadioGroup name="enableBatch" options={['是', '否']} defaultValue="否" />
        </RequiredField>

        <RequiredField label="测评学年">
          <select className="w-full border border-gray-300 rounded px-3 py-2.5 text-sm outline-none focus:border-[#1890ff] bg-white text-gray-700">
            <option>2025-2026</option>
            <option>2024-2025</option>
          </select>
        </RequiredField>

        <RequiredField label="测评学期">
          <select className="w-full border border-gray-300 rounded px-3 py-2.5 text-sm outline-none focus:border-[#1890ff] bg-white text-gray-700">
            <option>第一学期</option>
            <option>第二学期</option>
          </select>
        </RequiredField>

        <RequiredField label="参评对象设置">
          <select className="w-full border border-gray-300 rounded px-3 py-2.5 text-sm outline-none focus:border-[#1890ff] bg-white text-gray-700">
            <option>评奖评优基准名单</option>
            <option>2024级专科生基准名单</option>
          </select>
        </RequiredField>

        <RequiredField label="测评时间">
          <DateRangeInput />
        </RequiredField>

        <RequiredField label="加分项申请时间">
          <DateRangeInput />
        </RequiredField>

        <RequiredField label="是否启用申请须知">
          <RadioGroup
            name="enableNotice"
            options={['是', '否']}
            defaultValue={noticeEnabled}
            onChange={setNoticeEnabled}
          />
        </RequiredField>
      </div>

      {noticeEnabled === '是' && (
        <div>
          <RequiredField label="申请须知">
            <div className="border border-gray-300 rounded overflow-hidden">
              <div className="h-10 bg-[#fafafa] border-b border-gray-200 flex items-center px-3 text-sm text-gray-500 gap-4">
                <span>B</span>
                <span>I</span>
                <span>U</span>
                <span>段落</span>
              </div>
              <textarea
                placeholder="请输入加分项申请须知"
                className="w-full h-[160px] px-3 py-2 text-sm outline-none resize-none"
              />
            </div>
          </RequiredField>
        </div>
      )}

        <RequiredField label="加分项审核流程">
          <ReviewFlowTable />
        </RequiredField>
    </div>
  );
}

// 第二步：参评学生设置
function BatchStudentScopeStep({ studentScopeList }) {
  return (
    <div>
      <div className="flex items-center mb-5">
        <div className="flex gap-3">
          <button className="px-5 py-2 bg-[#1890ff] text-white rounded text-sm hover:bg-[#096dd9]">
            新增
          </button>
          <button className="px-5 py-2 border border-red-400 text-red-500 rounded text-sm hover:bg-red-50">
            删除
          </button>
        </div>
      </div>

      <div className="overflow-x-auto border border-gray-200 rounded">
        <table className="w-full text-left border-collapse text-[13px] text-gray-700 whitespace-nowrap">
          <thead>
            <tr className="bg-[#fafafa] border-b border-gray-200">
              <th className="py-3 px-4 w-10">
                <input type="checkbox" />
              </th>
              <TableHead>序号</TableHead>
              <TableHead>年级</TableHead>
              <TableHead>学生类别</TableHead>
            </tr>
          </thead>
          <tbody>
            {studentScopeList.map((row, index) => (
              <tr key={row.id} className="border-b border-gray-100 hover:bg-[#f8f9fa]">
                <td className="py-3 px-4">
                  <input type="checkbox" />
                </td>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{row.grade}</TableCell>
                <TableCell>{row.studentType}</TableCell>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end items-center mt-4 text-sm text-gray-700 gap-2">
        <span>第1-1条 / 共1条</span>
        <button className="text-gray-300 px-1">&lt;</button>
        <button className="px-2.5 py-1 border border-[#1890ff] text-[#1890ff] rounded">1</button>
        <button className="text-gray-300 px-1">&gt;</button>
        <select className="border border-gray-300 rounded px-2 py-1 outline-none bg-white">
          <option>10 条/页</option>
        </select>
      </div>
    </div>
  );
}

// 必填字段布局
function RequiredField({ label, children }) {
  return (
    <label className="grid grid-cols-[170px_minmax(0,1fr)] items-start gap-3 text-sm">
      <span className="text-right text-gray-800 pt-2.5">
        <span className="text-red-500 mr-1">*</span>
        {label}：
      </span>
      {children}
    </label>
  );
}

// 时间范围输入
function DateRangeInput() {
  return (
    <div className="flex items-center gap-3">
      <input
        type="datetime-local"
        className="flex-1 border border-gray-300 rounded px-3 py-2.5 text-sm outline-none focus:border-[#1890ff]"
      />
      <span className="text-gray-400">至</span>
      <input
        type="datetime-local"
        className="flex-1 border border-gray-300 rounded px-3 py-2.5 text-sm outline-none focus:border-[#1890ff]"
      />
    </div>
  );
}

// 加分项审核流程配置
function ReviewFlowTable() {
  const flowList = [
    {
      id: 1,
      name: '申请',
      checked: true,
      disabled: true,
      timeLimit: '不限',
    },
    {
      id: 2,
      name: '辅导员审核',
      checked: false,
      disabled: false,
    },
    {
      id: 3,
      name: '学院审核',
      checked: false,
      disabled: false,
    },
    {
      id: 4,
      name: '学校审核',
      checked: false,
      disabled: false,
    },
  ];

  return (
    <div className="overflow-x-auto border border-gray-200 rounded">
      <table className="w-full text-left border-collapse text-[13px] text-gray-700 whitespace-nowrap">
        <thead>
          <tr className="bg-[#fafafa] border-b border-gray-200">
            <TableHead>序号</TableHead>
            <TableHead>环节名称</TableHead>
            <TableHead>办理时间</TableHead>
          </tr>
        </thead>
        <tbody>
          {flowList.map((row) => (
            <tr key={row.id} className="border-b border-gray-100 hover:bg-[#f8f9fa]">
              <TableCell>{row.id}</TableCell>
              <TableCell>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    defaultChecked={row.checked}
                    disabled={row.disabled}
                  />
                  <span className={row.disabled ? 'text-gray-400' : 'text-gray-700'}>
                    {row.name}
                  </span>
                </label>
              </TableCell>
              <TableCell>
                <label className="inline-flex items-center gap-2">
                  <input type="checkbox" defaultChecked />
                  <span>不限</span>
                </label>
              </TableCell>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// 单选按钮组
function RadioGroup({ name, options, defaultValue, onChange }) {
  return (
    <div className="h-[42px] flex items-center gap-6">
      {options.map((option) => (
        <label key={option} className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name={name}
            defaultChecked={option === defaultValue}
            onChange={() => {
              if (onChange) {
                onChange(option);
              }
            }}
          />
          <span>{option}</span>
        </label>
      ))}
    </div>
  );
}

// 表格表头
function TableHead({ children }) {
  return (
    <th className="py-3 px-4 font-medium text-gray-800">
      {children}
    </th>
  );
}

// 表格单元格
function TableCell({ children }) {
  return (
    <td className="py-3 px-4">
      {children}
    </td>
  );
}
